import React, { useEffect, useState } from "react";
import { useContext } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Typography,
} from "@mui/material";
import LoadingBackdrop from "../components/loading-backdrop";
import { referralsApi, ReferralsGetResponse } from "../services/api/referrals";
import { getCustomLabel } from "../utils/custom-labels";
import { UserContext } from "../components/app-routes";
import Page from "../components/page";

function formatCents(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function ReferralsPage() {
  const context = useContext(UserContext);
  const grower = context.grower;

  const [busy, setBusy] = useState(false);
  const [data, setData] = useState<ReferralsGetResponse | null>(null);

  const load = async () => {
    setBusy(true);
    try {
      const res = await referralsApi.get();
      setData(res);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleEnroll = async () => {
    setBusy(true);
    try {
      const enrolled = await referralsApi.enroll();

      setData((prev) =>
        prev
          ? {
              ...prev,
              Enrolled: true,
              Code: enrolled.Code,
            }
          : {
              Enrolled: true,
              Code: enrolled.Code,
              TotalEarnedCents: 0,
              TotalPaidCents: 0,
              TotalOwedCents: 0,
              Conversions: [],
            },
      );

      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page title={getCustomLabel(grower ?? null, "referrals")}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {data?.Enrolled ? (
            <Box>
              <Typography sx={{ mb: 1 }}>Your referral code:</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {data.Code}
              </Typography>
              <Typography sx={{ mt: 2 }}>
                Share this code with fellow growers and encourage them to sign
                up with SeedLeaf! It gives them a 25% discount if they sign up
                AND links their sign up to your account.<br></br>
                <br></br>When they subscribe to Seedleaf on a paid plan, you
                earn referral credits:<br></br> - US$3 for each month they stay
                subscribed to a monthly Business Plan or higher (paid every 6
                months) OR<br></br> - US$36 per annual subscriber.
                <br></br>
                <br></br>You can apply credits to your subscription or we'll
                send you a payment directly.
                <br></br>
                <br></br>Track your progress below!
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography sx={{ mb: 2 }}>
                Join the SeedLeaf referral program to get your referral code.
              </Typography>
              <Button variant="contained" onClick={handleEnroll}>
                Enroll and Get My Code
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {data?.Enrolled && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Earnings
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Typography sx={{ mb: 1 }}>
              Total earned: {formatCents(data.TotalEarnedCents)}
            </Typography>
            <Typography sx={{ mb: 1 }}>
              Total paid: {formatCents(data.TotalPaidCents)}
            </Typography>
            <Typography sx={{ mb: 2 }}>
              Total owed: {formatCents(data.TotalOwedCents)}
            </Typography>

            <Typography sx={{ color: "text.secondary" }}>
              Payouts are processed manually
            </Typography>
          </CardContent>
        </Card>
      )}

      <LoadingBackdrop open={busy} />
    </Page>
  );
}
